import {Button} from '@primer/react'
import type {ButtonProps} from '@primer/react'
import {useCallback, useLayoutEffect, useRef} from 'react'
import type {Key, ReactNode, RefCallback} from 'react'
import {Disclosure} from '../disclosure/Disclosure'

export interface ShowMoreProps<T> {
  items: readonly T[]
  initialCount: number
  as?: 'ul' | 'ol'
  getItemKey: (item: T) => Key
  renderItem: (item: T, index: number) => ReactNode
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  moreLabel?: string
  lessLabel?: string
  showChevron?: boolean
  triggerVariant?: ButtonProps['variant']
  className?: string
  listClassName?: string
  visibleItemClassName?: string
  overflowItemClassName?: string
}

export function ShowMore<T>({
  items,
  initialCount,
  as = 'ul',
  getItemKey,
  renderItem,
  open,
  defaultOpen,
  onOpenChange,
  moreLabel,
  lessLabel = 'Show less',
  showChevron = true,
  triggerVariant = 'invisible',
  className,
  listClassName,
  visibleItemClassName,
  overflowItemClassName,
}: ShowMoreProps<T>) {
  const normalizedInitialCount = Number.isNaN(initialCount) ? 0 : Math.trunc(initialCount)
  const visibleCount = Math.max(0, Math.min(items.length, normalizedInitialCount))
  const visibleItems = items.slice(0, visibleCount)
  const overflowItems = items.slice(visibleCount)
  const focusTargetRef = useRef<HTMLUListElement | HTMLOListElement | null>(null)
  const overflowListRef = useRef<HTMLUListElement | HTMLOListElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const itemNodesRef = useRef(new Map<Key, HTMLLIElement>())
  const pendingItemFocusRef = useRef<{key: Key; focusableIndex: number} | null>(null)
  const removedOverflowHadFocusRef = useRef(false)
  const removedTriggerHadFocusRef = useRef(false)

  const setFocusTargetRef = useCallback((node: HTMLUListElement | HTMLOListElement | null) => {
    focusTargetRef.current = node
  }, [])

  const setOverflowListRef = useCallback((node: HTMLUListElement | HTMLOListElement | null) => {
    const previousOverflowList = overflowListRef.current

    if (node) {
      removedOverflowHadFocusRef.current = false
    } else if (previousOverflowList?.contains(previousOverflowList.ownerDocument.activeElement)) {
      removedOverflowHadFocusRef.current = true
    }

    overflowListRef.current = node
  }, [])

  const setTriggerRef = useCallback((node: HTMLButtonElement | null) => {
    const previousTrigger = triggerRef.current

    if (node) {
      // Disclosure.Trigger composes callback refs. A normal rerender can detach and immediately
      // reattach the same trigger, so only preserve the removal signal if no trigger replaces it.
      removedTriggerHadFocusRef.current = false
    } else if (previousTrigger?.ownerDocument.activeElement === previousTrigger) {
      removedTriggerHadFocusRef.current = true
    }

    triggerRef.current = node
  }, [])

  const setItemRef = useCallback((key: Key, node: HTMLLIElement | null) => {
    const previousItem = itemNodesRef.current.get(key)

    if (node) {
      itemNodesRef.current.set(key, node)
      return
    }

    if (previousItem) {
      const activeElement = previousItem.ownerDocument.activeElement
      if (activeElement instanceof HTMLElement && previousItem.contains(activeElement)) {
        pendingItemFocusRef.current = {
          key,
          focusableIndex: getFocusableElements(previousItem).indexOf(activeElement),
        }
      }
    }

    itemNodesRef.current.delete(key)
  }, [])

  useLayoutEffect(() => {
    const pendingItemFocus = pendingItemFocusRef.current
    let restoredItemFocus = false

    if (pendingItemFocus) {
      const item = itemNodesRef.current.get(pendingItemFocus.key)
      const itemIsHidden = item?.closest('[aria-hidden="true"]')
      const target =
        item && !itemIsHidden
          ? (getFocusableElements(item)[pendingItemFocus.focusableIndex] ?? triggerRef.current)
          : (triggerRef.current ?? focusTargetRef.current)

      target?.focus()
      restoredItemFocus = target !== undefined && target !== null
      pendingItemFocusRef.current = null
    }

    if (
      !restoredItemFocus &&
      overflowItems.length === 0 &&
      (removedTriggerHadFocusRef.current || removedOverflowHadFocusRef.current)
    ) {
      focusTargetRef.current?.focus()
    }

    removedOverflowHadFocusRef.current = false
    removedTriggerHadFocusRef.current = false
  })

  if (overflowItems.length === 0) {
    return (
      <div className={className}>
        <ItemList
          ref={setFocusTargetRef}
          as={as}
          items={items}
          getItemKey={getItemKey}
          renderItem={renderItem}
          className={listClassName}
          itemClassName={visibleItemClassName}
          onItemRef={setItemRef}
          tabIndex={-1}
        />
      </div>
    )
  }

  return (
    <div className={className}>
      <Disclosure open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
        <ItemList
          as={as}
          items={visibleItems}
          getItemKey={getItemKey}
          renderItem={renderItem}
          className={listClassName}
          itemClassName={visibleItemClassName}
          onItemRef={setItemRef}
        />
        <Disclosure.Panel>
          <ItemList
            ref={setOverflowListRef}
            as={as}
            items={overflowItems}
            startIndex={visibleCount}
            getItemKey={getItemKey}
            renderItem={renderItem}
            className={listClassName}
            itemClassName={overflowItemClassName}
            onItemRef={setItemRef}
          />
        </Disclosure.Panel>
        <Disclosure.Trigger
          ref={setTriggerRef}
          render={(triggerProps, state) => (
            <Button
              {...triggerProps}
              variant={triggerVariant}
              size="small"
              trailingVisual={showChevron ? Disclosure.Chevron : undefined}
            >
              {state.open ? lessLabel : (moreLabel ?? `Show ${overflowItems.length} more`)}
            </Button>
          )}
        />
      </Disclosure>
    </div>
  )
}

interface ItemListProps<T> {
  ref?: RefCallback<HTMLUListElement | HTMLOListElement>
  as: 'ul' | 'ol'
  items: readonly T[]
  startIndex?: number
  getItemKey: (item: T) => Key
  renderItem: (item: T, index: number) => ReactNode
  className?: string
  itemClassName?: string
  onItemRef: (key: Key, node: HTMLLIElement | null) => void
  tabIndex?: -1
}

function ItemList<T>({
  ref,
  as,
  items,
  startIndex = 0,
  getItemKey,
  renderItem,
  className,
  itemClassName,
  onItemRef,
  tabIndex,
}: ItemListProps<T>) {
  const children = items.map((item, localIndex) => {
    const index = startIndex + localIndex
    const key = getItemKey(item)
    return (
      <ItemListItem key={key} itemKey={key} className={itemClassName} onItemRef={onItemRef}>
        {renderItem(item, index)}
      </ItemListItem>
    )
  })

  if (as === 'ol') {
    return (
      <ol ref={ref} className={className} start={startIndex > 0 ? startIndex + 1 : undefined} tabIndex={tabIndex}>
        {children}
      </ol>
    )
  }

  return (
    <ul ref={ref} className={className} tabIndex={tabIndex}>
      {children}
    </ul>
  )
}

interface ItemListItemProps {
  itemKey: Key
  className?: string
  onItemRef: (key: Key, node: HTMLLIElement | null) => void
  children: ReactNode
}

function ItemListItem({itemKey, className, onItemRef, children}: ItemListItemProps) {
  const ref = useCallback((node: HTMLLIElement | null) => onItemRef(itemKey, node), [itemKey, onItemRef])
  return (
    <li ref={ref} className={className}>
      {children}
    </li>
  )
}

const getFocusableElements = (item: HTMLElement) =>
  Array.from(
    item.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])',
    ),
  )
